// Excel içe aktarma: dosya seç → sunucuda ön okuma (özet + ilk 5 satır) → listeyi kaydet.
import React, { useRef, useState } from 'react';
import { parseContactFile, uploadContactList } from '../../util/contacts';
import { Modal, Btn, Field, Input, Banner, Spinner, Icon, COLORS, num } from './ui';

export default function ImportModal({ onClose, onImported, t }) {
	const tr = t || ((s, o) => (o ? s.replace(/{{(\w+)}}/g, (a, k) => o[k]) : s));
	const [file, setFile] = useState(null);
	const [parsed, setParsed] = useState(null);
	const [parsing, setParsing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [err, setErr] = useState('');
	const [name, setName] = useState('');
	const fileRef = useRef(null);

	function onFile(e) {
		const f = e.target.files && e.target.files[0];
		e.target.value = '';
		if (!f) return;
		setFile(f);
		setErr('');
		setParsed(null);
		setParsing(true);
		setName(f.name.replace(/\.[^.]+$/, ''));
		parseContactFile(f)
			.then((data) => {
				if (!data || !data.success) throw new Error((data && data.error) || '');
				const rows = data.rows || [];
				if (!rows.length) throw new Error(tr('Dosyada telefon numarası olan satır bulunamadı.'));
				const total = data.total || rows.length;
				const skipped = data.skipped || 0;
				setParsed({ rows, total, skipped, duplicates: Math.max(0, total - rows.length - skipped) });
			})
			.catch((e2) => setErr(e2.message || tr('Dosya okunamadı. .xlsx, .xls veya .csv biçiminde bir dosya seçin.')))
			.finally(() => setParsing(false));
	}
	function save() {
		setSaving(true);
		setErr('');
		uploadContactList(file, 'contactList', false, name.trim() || file.name)
			.then((data) => {
				if (!data || !data.success) throw new Error((data && data.error) || tr('Liste kaydedilemedi.'));
				onImported && onImported(data);
			})
			.catch((e2) => {
				setErr(e2.message || tr('Liste kaydedilemedi.'));
				setSaving(false);
			});
	}
	const stats = parsed
		? [
				[tr('Satır'), parsed.total, COLORS.navy],
				[tr('Benzersiz numara'), parsed.rows.length, COLORS.primary],
				[tr('Yinelenen satır'), parsed.duplicates, parsed.duplicates ? COLORS.warn : COLORS.navy],
				[tr('Geçersiz'), parsed.skipped, parsed.skipped ? COLORS.danger : COLORS.navy],
		  ]
		: [];
	return (
		<Modal
			title={tr('Yeni Excel yükle')}
			desc={tr('İlk satır sütun başlıkları olmalı (Phone / Telefon, Name / İsim, Label / Etiket). Yinelenen numaralar tek kayda indirilir.')}
			icon="sheet"
			width={640}
			onClose={onClose}
			busy={saving}
			footLeft={
				<a href="/contacts_example.xlsx" download="contacts_example.xlsx" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary, fontWeight: 600 }}>
					{tr('Örnek dosyayı indir')}
				</a>
			}
			footer={
				<>
					<Btn variant="ghost" onClick={onClose} disabled={saving}>
						{tr('Vazgeç')}
					</Btn>
					<Btn onClick={save} busy={saving} disabled={!parsed || !name.trim()}>
						{parsed ? tr('{{n}} kişiyi içe aktar', { n: num(parsed.rows.length) }) : tr('İçe aktar')}
					</Btn>
				</>
			}
		>
			<div className="wsm-col">
				<input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} style={{ display: 'none' }} />
				<div
					role="button"
					tabIndex={0}
					onClick={() => fileRef.current && fileRef.current.click()}
					onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileRef.current.click()}
					style={{ border: '1px dashed #B9C3CD', borderRadius: 10, padding: 18, display: 'flex', alignItems: 'center', gap: 14, background: '#FAFBFC', cursor: 'pointer' }}
				>
					<div style={{ width: 48, height: 48, borderRadius: 10, background: '#E7F4EE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
						{parsing ? <Spinner size={20} /> : <Icon name="upload" size={20} color={COLORS.primary} />}
					</div>
					<div style={{ flex: 1, minWidth: 0, fontSize: 13, color: COLORS.slate, lineHeight: 1.5 }}>
						<strong style={{ color: COLORS.navy, wordBreak: 'break-all' }}>{file ? file.name : tr('Dosya seçin')}</strong>
						<br />
						{file
							? parsed
								? `${num(parsed.total)} ${tr('satır')}`
								: parsing
								? tr('Okunuyor…')
								: ''
							: tr('.xlsx, .xls veya .csv dosyası seçin.')}
					</div>
					<Btn variant="ghost" size="s">
						{tr('Dosya seç')}
					</Btn>
				</div>
				{err ? <Banner tone="danger">{err}</Banner> : null}
				{parsed ? (
					<>
						<Field label={tr('Liste adı')} required>
							<Input value={name} onChange={setName} placeholder={tr('Ör. Toptancılar Ekim')} maxLength={255} />
						</Field>
						<div className="wsm-mini-stats">
							{stats.map(([l, v, c]) => (
								<div key={l} className="wsm-mini-stat">
									<div>{l}</div>
									<div style={{ color: c }}>{num(v)}</div>
								</div>
							))}
						</div>
						{parsed.duplicates > 0 ? (
							<Banner>
								<strong>
									{num(parsed.duplicates)} {tr('yinelenen satır bulundu.')}
								</strong>{' '}
								{tr('Aynı numara birden fazla satırda geçiyor; her numaraya yalnızca bir kez gönderilir.')}
							</Banner>
						) : null}
						<div style={{ border: '1px solid #E2E7EC', borderRadius: 10, overflow: 'hidden' }}>
							<div className="wsm-table-wrap">
								<table className="wsm-table">
									<thead>
										<tr>
											<th>{tr('İsim')}</th>
											<th>{tr('Numara')}</th>
											<th>{tr('Etiket')}</th>
										</tr>
									</thead>
									<tbody>
										{parsed.rows.slice(0, 5).map((r, i) => (
											<tr key={i}>
												<td>{r.Name || <span className="wsm-faint">—</span>}</td>
												<td className="wsm-muted wsm-nowrap">{r.Phone}</td>
												<td className="wsm-muted">{(r.tags || []).join(', ') || '—'}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<div style={{ padding: '8px 24px', fontSize: 12, color: COLORS.muted, borderTop: '1px solid #EEF2F5' }}>{tr('İlk 5 kayıt gösteriliyor.')}</div>
						</div>
					</>
				) : null}
			</div>
		</Modal>
	);
}
